// Transitions PR-2 — the BMSP covenant countdown card on a balloon loan's
// Transitions tab: months/days to the Month-72 window, the Month-73
// application deadline (a BLA covenant), and balloon maturity — the richer
// server-derived surface next to VitalsStrip's client-side "N months to
// balloon" line. Renders nothing for non-balloon loans (null dto).

import type { ReactNode } from "react";
import { Card, StatusBadge, type StatusTone } from "@/design-system/components/ui";
import type { BmspCountdownDto } from "@/api/portfolioAdmin/loanTransitionsApi";

function fmtDate(value: string | null): string {
  if (!value) return "—";
  const d = new Date(`${value.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

function daysBadge(days: number | null, label: string): ReactNode {
  if (days == null) return <span className="text-gray-mid">—</span>;
  const tone: StatusTone =
    days < 0 ? "muted" : days <= 30 ? "danger" : days <= 90 ? "warn" : "info";
  const text =
    days < 0
      ? `${label} passed`
      : days === 0
        ? `${label} today`
        : `${days}d`;
  return <StatusBadge tone={tone}>{text}</StatusBadge>;
}

export function BmspCountdownCard({ countdown }: { countdown: BmspCountdownDto }) {
  return (
    <Card data-testid="bmsp-countdown-card">
      <div className="flex flex-wrap items-center justify-between gap-space-2">
        <div>
          <h4 className="font-display font-bold text-h4 m-0">
            Balloon maturity countdown (BMSP)
          </h4>
          <p className="text-small text-gray-mid m-0">
            Month {countdown.currentMonthNumber} of {countdown.termMonths}. The BMSP
            application deadline is month 73 — a loan-agreement covenant.
          </p>
        </div>
        {countdown.windowMissed && (
          <StatusBadge tone="danger" data-testid="bmsp-window-missed">
            Month-73 window missed
          </StatusBadge>
        )}
        {countdown.hasOpenBmspTransition && (
          <StatusBadge tone="progress" data-testid="bmsp-transition-open">
            BMSP transition open
          </StatusBadge>
        )}
      </div>

      <div className="mt-space-3 grid grid-cols-1 md:grid-cols-3 gap-space-3 text-small">
        {countdown.hasBmspWindow && (
          <div data-testid="bmsp-window-open-cell">
            <p className="m-0 font-semibold">BMSP window opens</p>
            <p className="m-0 text-gray-mid">{fmtDate(countdown.windowOpen)} (month 72)</p>
            {daysBadge(countdown.daysToWindowOpen, "Opened")}
          </div>
        )}
        {countdown.hasBmspWindow && (
          <div data-testid="bmsp-deadline-cell">
            <p className="m-0 font-semibold">Application deadline</p>
            <p className="m-0 text-gray-mid">
              {fmtDate(countdown.applicationDeadline)} (month 73)
            </p>
            {daysBadge(countdown.daysToDeadline, "Deadline")}
          </div>
        )}
        <div data-testid="bmsp-maturity-cell">
          <p className="m-0 font-semibold">Balloon maturity</p>
          <p className="m-0 text-gray-mid">
            {fmtDate(countdown.balloonMaturity)} (month {countdown.termMonths})
          </p>
          {daysBadge(countdown.daysToMaturity, "Maturity")}
        </div>
      </div>

      <p className="text-small text-gray-mid m-0 mt-space-3">
        The three BMSP paths — full payoff, third-party refinancing, or a
        note-holder-approved extension (1–5 years; the rate never reprices) —
        are started from “Start a transition” above.
      </p>
    </Card>
  );
}
